/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* cp-focal13-product-migration-v1 — scoped components; no legacy global selectors. */
(() => {
  'use strict';
  const MAX_STRAPS = 3;
  const tagsOf = (product) => Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',').map((tag) => tag.trim());
  const hasTag = (product, tag) => tagsOf(product).includes(tag);

  function parseKitConfiguration(config) {
    const handles = [];
    const quantities = new Map();
    for (const tag of config.tags || []) {
      const parts = String(tag).split(':');
      if (parts[0] === 'handle') {
        if (parts.length !== 2 || !/^[a-z0-9][a-z0-9-]*$/.test(parts[1]) || handles.includes(parts[1])) {
          throw new Error('Componente de kit inválido ou repetido.');
        }
        handles.push(parts[1]);
      } else if (parts[0] === 'qty') {
        const quantity = Number(parts[2]);
        if (parts.length !== 3 || !/^[23]$/.test(parts[2]) || quantities.has(parts[1])) {
          throw new Error('Quantidade de componente inválida.');
        }
        quantities.set(parts[1], quantity);
      }
    }
    if (handles.length < 2 || handles.length > 4 || [...quantities.keys()].some((handle) => !handles.includes(handle))) {
      throw new Error('O kit deve conter uma case e até três alças.');
    }
    if (!Array.isArray(config.components) || config.components.length !== handles.length) {
      throw new Error('A lista de componentes do kit está incompleta.');
    }
    return handles.map((handle) => {
      const matches = config.components.filter((component) => component.handle === handle);
      if (matches.length !== 1 || !matches[0].product || !Array.isArray(matches[0].product.variants)) {
        throw new Error('Produto do kit indisponível: ' + handle + '.');
      }
      return { ...matches[0], quantity: quantities.get(handle) || 1 };
    });
  }

  function validateKitComponents(components) {
    const cases = components.filter((component) => hasTag(component.product, 'custom_variant') && !hasTag(component.product, 'modelo_estoque'));
    const straps = components.filter((component) => hasTag(component.product, 'modelo_estoque') && !hasTag(component.product, 'custom_variant'));
    if (cases.length !== 1 || cases[0].quantity !== 1 || cases.length + straps.length !== components.length) {
      throw new Error('O kit precisa conter uma case e somente alças de estoque.');
    }
    const units = straps.reduce((sum, component) => sum + component.quantity, 0);
    if (units < 1 || units > MAX_STRAPS) throw new Error('O kit precisa ter entre uma e três alças.');
    if (straps.some((component) => component.stockSufficient !== true || component.product.variants.length !== 1 || component.product.variants[0].available !== true)) {
      throw new Error('Uma das alças deste kit está indisponível.');
    }
    const availableCases = cases[0].product.variants.filter((variant) => variant.available === true && variant.title !== '-');
    if (!availableCases.length) throw new Error('A case deste kit está indisponível.');
    return { caseComponent: cases[0], straps, availableCases };
  }

  function buildKitItems(topology, variantId) {
    const variant = topology.availableCases.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant || variant.available !== true || !variant.title || variant.title === '-') {
      throw new Error('Selecione um modelo disponível.');
    }
    return [
      { id: variant.id, quantity: 1 },
      ...topology.straps.map((component) => ({
        id: component.product.variants[0].id,
        quantity: component.quantity,
        properties: { Modelo: variant.title }
      }))
    ];
  }

  // Pure helpers also run under Node for the migration's isolated regression tests.
  globalThis.CPPapayaKit = Object.freeze({ parseKitConfiguration, validateKitComponents, buildKitItems });
  if (typeof document === 'undefined') return;

  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'BRL'
  }).format(cents / 100);

  class CPPapayaProductController extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.pending = false;
      const signal = this.abortController.signal;
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.form = document.getElementById(this.getAttribute('form-id'));
        if (!this.form || this.hasAttribute('data-traditional-kit')) return;
        document.addEventListener('change', (event) => {
          const target = event.target;
          if (this.contains(target) && target.matches('[data-cp-option-select]')) {
            const hidden = this.querySelector(`input[type="hidden"][data-option-position="${target.dataset.optionPosition}"]`);
            if (hidden && target.value) {
              hidden.value = target.value;
              hidden.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (target.form === this.form && target.matches('[data-option-position]')) {
            this.pending = true;
            this.syncButtons();
          }
        }, { capture: true, signal });
        this.form.addEventListener('product:rerender', () => {
          queueMicrotask(() => {
            this.pending = false;
            this.syncButtons();
          });
        }, { signal });
        // Capture blocks Enter and sticky-form submissions as well as main button clicks.
        document.addEventListener('submit', (event) => {
          if (event.target === this.form && !this.selectionReady()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.querySelector('[data-cp-model-select]')?.focus();
          }
        }, { capture: true, signal });
        this.observer = new MutationObserver(() => this.syncButtons());
        this.observer.observe(this, { childList: true, subtree: true });
        this.syncButtons();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
    }

    selectionReady() {
      const modelSelect = this.querySelector('[data-cp-model-select]');
      return !this.pending && (!this.hasAttribute('data-requires-model') || Boolean(modelSelect?.value));
    }

    syncButtons() {
      if (!this.form || !this.isConnected) return;
      const ready = this.selectionReady();
      const available = this.querySelector('[data-cp-variant-available]')?.dataset.cpVariantAvailable === 'true';
      for (const button of this.form.elements) {
        if (button.type !== 'submit') continue;
        button.disabled = !ready || !available || button.getAttribute('aria-busy') === 'true';
      }
      for (const payment of this.querySelectorAll('.shopify-payment-button, [data-cp-payment]')) payment.hidden = !ready || !available;
    }
  }

  class CPPapayaKitSelector extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.busy = false;
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() { this.abortController?.abort(); }

    initialize() {
      if (!this.isConnected) return;
      this.scope = this.closest('cp-product-controller');
      this.select = this.querySelector('[data-cp-kit-model]');
      this.button = this.scope?.querySelector('[data-cp-kit-add]');
      this.errorElement = this.scope?.querySelector('[data-cp-kit-error]');
      if (!this.scope || !this.select || !this.button) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-cp-kit-config]').textContent);
        this.components = parseKitConfiguration(this.config);
        this.topology = validateKitComponents(this.components);
        this.select.replaceChildren(new Option('Selecione o modelo', '', true, true));
        this.select.options[0].disabled = true;
        for (const variant of this.topology.availableCases) this.select.add(new Option(variant.title, String(variant.id)));
        const selected = this.topology.availableCases.find((variant) => variant.title === this.config.selectedModel);
        if (selected) this.select.value = String(selected.id);
        this.select.disabled = false;
        this.select.addEventListener('change', () => this.updateSelection(true), { signal: this.abortController.signal });
        this.button.addEventListener('click', () => this.addToCart(), { signal: this.abortController.signal });
        this.updateSelection(false);
      } catch (error) {
        this.fail(error.message);
        this.button.textContent = 'Indisponível';
        this.select.replaceChildren(new Option('Kit indisponível', ''));
      }
    }

    fail(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    updateSelection(updateUrl) {
      const variant = this.topology.availableCases.find((item) => String(item.id) === this.select.value);
      this.button.disabled = this.busy;
      this.button.textContent = this.busy ? 'Adicionando…' : 'Adicione ao carrinho';
      if (updateUrl && variant) {
        this.select.removeAttribute('aria-invalid');
        this.errorElement.hidden = true;
      }
      const priceVariant = variant || this.topology.availableCases[0];
      const total = Number(priceVariant.price) + this.topology.straps.reduce((sum, component) => sum + Number(component.product.variants[0].price) * component.quantity, 0);
      const priceList = this.closest('.product__info')?.querySelector('[data-cp-kit-price-container] .price-list');
      if (priceList && Number.isSafeInteger(total)) {
        const price = document.createElement('span');
        price.className = 'price price--large';
        price.textContent = formatMoney(total);
        priceList.replaceChildren(price);
      }
      if (variant && updateUrl && this.hasAttribute('update-url')) {
        const parent = this.config.parentVariants.find((item) => item.title === variant.title);
        if (parent) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', parent.id);
          window.history.replaceState({}, '', url);
        }
      }
    }

    async addToCart() {
      if (this.busy) return;
      if (!this.select.value) {
        this.fail('Escolha o modelo do seu iPhone para adicionar ao carrinho.');
        this.select.setAttribute('aria-invalid', 'true');
        this.select.focus();
        return;
      }
      this.busy = true;
      this.errorElement.hidden = true;
      this.updateSelection(false);
      let cartWasAdded = false;
      let navigatingToCart = false;
      const goToCart = () => {
        navigatingToCart = true;
        window.location.assign(`${shopRoot()}cart`);
      };
      try {
        // Recheck component availability. Shopify remains authoritative for stock at cart/add.
        const components = await Promise.all(this.components.map(async (component) => {
          const response = await fetch(`${shopRoot()}products/${encodeURIComponent(component.handle)}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error('Não foi possível conferir os componentes. Tente novamente.');
          return { ...component, product: await response.json() };
        }));
        const topology = validateKitComponents(components);
        const items = buildKitItems(topology, this.select.value);
        const response = await fetch(`${shopRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['mini-cart'], sections_url: window.location.pathname })
        });
        // Once Shopify confirms success, a UI/JSON refresh failure must never invite another addition.
        cartWasAdded = response.ok;
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || 'Não foi possível adicionar o kit.');
        if (window.themeVariables?.settings?.cartType === 'page') {
          goToCart();
          return;
        }
        this.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: added.items?.[0], items: added.items } }));
        const cartResponse = await fetch(`${shopRoot()}cart.js`, { cache: 'no-store' });
        if (!cartResponse.ok) {
          goToCart();
          return;
        }
        const cart = await cartResponse.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        const sectionHtml = added.sections?.['mini-cart'];
        if (typeof sectionHtml === 'string' && sectionHtml.trim()) {
          const sectionTemplate = document.createElement('template');
          sectionTemplate.innerHTML = sectionHtml;
          if (sectionTemplate.content.querySelector('cart-drawer .drawer__content')) cart.sections = added.sections;
        }
        // Without valid section HTML, Focal fetches the cart section using its native fallback.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true, detail: { cart, openMiniCart: window.themeVariables?.settings?.cartType === 'drawer' }
        }));
      } catch (error) {
        if (cartWasAdded) goToCart();
        else this.fail(error.message || 'Não foi possível adicionar o kit. Confira seu carrinho antes de tentar novamente.');
      } finally {
        if (!navigatingToCart) {
          this.busy = false;
          this.updateSelection(false);
        }
      }
    }
  }

  if (!customElements.get('cp-product-controller')) customElements.define('cp-product-controller', CPPapayaProductController);
  if (!customElements.get('cp-kit-selector')) customElements.define('cp-kit-selector', CPPapayaKitSelector);
})();
